import { DropdownFormFieldComponent, TextAreaFormFieldComponent, TextFormFieldComponent } from '@vivli/shared/components';
import { DisclosureStatusEnum, DisclosureTypeEnum, DisclosureTypeEnumKey } from '@vivli/features/disclosures/infrastructure/enum';
import { CSSProperties, useEffect } from 'react';
import { useEnumDropdownMapper } from '@vivli/shared/infrastructure/hook';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDisclosureContext } from '@vivli/features/disclosures/infrastructure/context';
import { IDisclosure } from '@vivli/features/disclosures/infrastructure/interface';

const containerStyle: CSSProperties = {
    display: 'grid',
    gridTemplateRows: 'auto',
    rowGap: '20px',
};

const sectionStyle = (gridTemplateColumns: string): CSSProperties => ({
    display: 'grid',
    gridTemplateColumns,
    columnGap: '1em',
    rowGap: '1em',
});

export const DisclosureInformationFormView = () => {
    const formApi = useFormContext<IDisclosure>();
    const { permissions, disclosure } = useDisclosureContext();
    const disclosureTypes = useEnumDropdownMapper(DisclosureTypeEnumKey, true);
    const disclosureType = useWatch({ name: 'disclosureType' });

    useEffect(() => {
        if (disclosureType !== DisclosureTypeEnum.Other) {
            return;
        }

        // setting the value to empty string forces validation so submit button gets disabled
        formApi.resetField('otherDisclosureType', { defaultValue: disclosure?.otherDisclosureType || '' });
        formApi.setFocus('otherDisclosureType');
    }, [disclosureType]);

    useEffect(() => {
        if (disclosure.status !== DisclosureStatusEnum.Draft) {
            return;
        }

        // focus title on load
        formApi.setFocus('title');
    }, []);

    return (
        <div style={containerStyle}>
            <div>
                As per the Data Use Agreement (DUA), Researcher(s) should send any public disclosures (manuscripts, abstracts, posters,
                slides, etc.) to Data Contributor(s) via this form at least 30 days prior to submission of materials to a learned forum or
                journal. As per the DUA, during the 30-day review period, Data Contributor(s) may provide non-binding comments regarding the
                scientific content. They may also possibly request the deletion of any confidential information (as defined in the DUA).
            </div>

            <div>
                <TextFormFieldComponent label={'Disclosure Title'} name={'title'} readonly={!permissions.editDisclosureInfo} />
            </div>

            <div style={sectionStyle(disclosureType === DisclosureTypeEnum.Other ? '1fr 1fr 1fr' : '1fr 1fr')}>
                <DropdownFormFieldComponent
                    selectText={'...'}
                    label={'Select a Disclosure Type'}
                    items={disclosureTypes}
                    name={'disclosureType'}
                    readonly={!permissions.editDisclosureInfo}
                />

                <div style={{ display: disclosureType === DisclosureTypeEnum.Other ? 'initial' : 'none' }}>
                    <TextFormFieldComponent
                        label={'Please enter your Disclosure Type'}
                        name={'otherDisclosureType'}
                        readonly={!permissions.editDisclosureInfo}
                    />
                </div>

                <TextFormFieldComponent
                    name={'journalOrConferenceName'}
                    label="Journal/Conference/Other"
                    readonly={!permissions.editDisclosureInfo}
                />
            </div>

            <div>
                <TextAreaFormFieldComponent
                    name={'researcherComment'}
                    label={'Researcher Comments'}
                    readonly={!permissions.editDisclosureInfo}
                />
            </div>
        </div>
    );
};
